import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

async function _fetchPerms() {
  try {
    const { data } = await api.get("/permissions/me");
    return Array.isArray(data?.permissions) ? data.permissions : [];
  } catch (e) {
    return [];
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sw_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        // Preserve the permission list across page reloads so the sidebar
        // doesn't flicker to "role-only" before the fetch completes.
        const cached = localStorage.getItem("sw_perms");
        if (cached) {
          try { setPermissions(JSON.parse(cached)); } catch { /* ignore */ }
        }
        const perms = await _fetchPerms();
        setPermissions(perms);
        localStorage.setItem("sw_perms", JSON.stringify(perms));
      })
      .catch(() => {
        localStorage.removeItem("sw_token");
        localStorage.removeItem("sw_user");
        localStorage.removeItem("sw_perms");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("sw_token", data.token);
    localStorage.setItem("sw_user", JSON.stringify(data.user));
    setUser(data.user);
    const perms = await _fetchPerms();
    setPermissions(perms);
    localStorage.setItem("sw_perms", JSON.stringify(perms));
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("sw_token");
    localStorage.removeItem("sw_user");
    localStorage.removeItem("sw_perms");
    setUser(null);
    setPermissions([]);
    window.location.href = "/login";
  };

  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    const perms = await _fetchPerms();
    setPermissions(perms);
    localStorage.setItem("sw_perms", JSON.stringify(perms));
    return data;
  };

  // Public: check if the current user has a permission key.
  // Also accepts an array of keys — returns true if ANY match.
  const hasPerm = useCallback((keyOrKeys) => {
    if (!keyOrKeys) return true;
    const list = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
    return list.some((k) => permissions.includes(k));
  }, [permissions]);

  const hasAllPerms = useCallback((keys) => {
    if (!keys?.length) return true;
    return keys.every((k) => permissions.includes(k));
  }, [permissions]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, permissions, hasPerm, hasAllPerms }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
